// src/features/banquetOwnerPanel/components/formPrimitives.jsx
//
// Presentational building blocks for the 16-section banquet form, shared by the
// owner-login dashboard (BanquetOwnerDashboardPage.jsx) and the public token-based
// owner-fill form (OwnerFillFormPage.jsx). No data-loading, auth, or save logic lives
// here — everything is driven by props.

import React, { useRef, useState } from "react";
import { FaArrowLeft, FaArrowRight, FaCheck, FaCloudArrowUp, FaFilePdf, FaStar } from "react-icons/fa6";
import { dateKey, statusLabels, statusStyles, toBookedSlots } from "../model/banquetFormModel.js";

export const inputClass =
  "min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100";
export const labelClass = "mb-1.5 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500";

export function Field({ label, required = false, children, className = "" }) {
  return (
    <label className={className}>
      <span className={labelClass}>{label}{required ? " *" : ""}</span>
      {children}
    </label>
  );
}

export function SelectField({ label, required, value, onChange, options }) {
  return (
    <Field label={label} required={required}>
      <select className={inputClass} value={value || ""} onChange={(event) => onChange(event.target.value)}>
        <option value="">Select</option>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </Field>
  );
}

export function TextField({ label, required, value, onChange, type = "text", className = "", placeholder = "" }) {
  return (
    <Field label={label} required={required} className={className}>
      <input className={inputClass} type={type} value={value || ""} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
    </Field>
  );
}

// Read-only counterpart to TextField, used for fields Google already supplied (name,
// address, rating, lat/long) on the public owner-fill form — same label styling, but a
// plain display box instead of an input so it's unmistakably not editable.
export function ReadOnlyField({ label, value, className = "" }) {
  return (
    <Field label={label} className={className}>
      <div className={`${inputClass} flex items-center bg-slate-100 text-slate-500`}>{value || "—"}</div>
    </Field>
  );
}

export function Toggle({ label, value, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!value)} className={`min-h-[44px] rounded-xl border px-4 py-2 text-sm font-bold transition ${value ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
      {label}: {value ? "Yes" : "No"}
    </button>
  );
}

const unique = (items) => [...new Set((items || []).filter(Boolean))];

export function ChipGroup({ label, options, value, onChange, required = false, multi = true, bulkActions = false, helperText = "" }) {
  const activeValues = Array.isArray(value) ? value : value ? [value] : [];
  const selectAll = () => onChange(unique([...activeValues, ...options]));
  const clearAll = () => onChange(activeValues.filter((item) => !options.includes(item)));
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className={labelClass}>{label}{required ? " *" : ""}</p>
          {helperText ? <p className="-mt-1 text-xs font-semibold text-slate-500">{helperText}</p> : null}
        </div>
        {multi && bulkActions ? (
          <div className="flex gap-2">
            <button type="button" onClick={selectAll} className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-black text-emerald-700">Select All</button>
            <button type="button" onClick={clearAll} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-600">Clear All</button>
          </div>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = activeValues.includes(option);
          return (
            <button
              type="button"
              key={option}
              onClick={() => {
                if (!multi) {
                  onChange(active ? "" : option);
                  return;
                }
                onChange(active ? activeValues.filter((item) => item !== option) : [...activeValues, option]);
              }}
              className={`min-h-[40px] rounded-full border px-4 py-2 text-sm font-bold transition ${active ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-slate-100 text-slate-700 hover:border-emerald-300 hover:bg-emerald-50"}`}
            >
              {active ? <FaCheck className="mr-1 inline text-xs" /> : null}{option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function Section({ index, title, children }) {
  return (
    <section id={`owner-section-${index}`} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <div className="mb-5 flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-sm font-black text-emerald-700">{index}</span>
        <h2 className="text-lg font-black text-slate-950 sm:text-xl">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export function MediaUploader({ label, items, max, accept, onAdd, onChangeItem, onDelete, onMove, onPrimary, primaryIndex, video = false }) {
  const addInputRef = useRef(null);
  const changeInputRefs = useRef({});
  const handleAddFiles = (event) => {
    try {
      onAdd(Array.from(event.target.files || []));
    } catch (selectionError) {
      console.error("Unable to select media files.", selectionError);
    } finally {
      event.target.value = "";
    }
  };
  const handleChangeFile = (event, index) => {
    try {
      onChangeItem(index, event.target.files?.[0]);
    } catch (selectionError) {
      console.error("Unable to change media file.", selectionError);
    } finally {
      event.target.value = "";
    }
  };
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className={labelClass}>{label}</p>
        <button type="button" onClick={() => addInputRef.current?.click()} className="inline-flex min-h-[40px] touch-manipulation items-center gap-2 rounded-xl bg-slate-950 px-4 py-2 text-sm font-bold text-white">
          <FaCloudArrowUp /> Select files
        </button>
        <input ref={addInputRef} type="file" multiple accept={accept} className="hidden" onChange={handleAddFiles} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((item, index) => {
          const src = item.previewUrl || item.url;
          return (
            <div key={item.id || item.url || index} className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
              <div className="relative aspect-[4/3] bg-slate-200">
                {video ? <video src={src} controls className="h-full w-full object-cover" /> : <img src={src} alt={item.name || label} className="h-full w-full object-cover" />}
                {primaryIndex === index ? <span className="absolute left-2 top-2 rounded-full bg-amber-400 px-2 py-1 text-xs font-black text-slate-950"><FaStar className="mr-1 inline" />Primary</span> : null}
              </div>
              <div className="space-y-2 p-3">
                <p className="truncate text-xs font-bold text-slate-700">{item.name || "Selected media"}</p>
                {item.status === "uploading" ? <p className="text-xs font-bold text-sky-700">Uploading{item.progress ? ` ${item.progress}%` : "..."} </p> : null}
                {item.status === "failed" ? <p className="break-words text-xs font-bold text-rose-700">{item.error || "Upload failed"}</p> : null}
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => changeInputRefs.current[index]?.click()} className="min-h-[34px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700">Change</button>
                  <input ref={(node) => { changeInputRefs.current[index] = node; }} type="file" accept={accept} className="hidden" onChange={(event) => handleChangeFile(event, index)} />
                  <button type="button" onClick={() => onDelete(index)} className="min-h-[34px] rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-bold text-rose-700">Delete</button>
                  {onMove ? <button type="button" onClick={() => onMove(index, -1)} className="min-h-[34px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700">Move Up</button> : null}
                  {onMove ? <button type="button" onClick={() => onMove(index, 1)} className="min-h-[34px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700">Move Down</button> : null}
                  {onPrimary ? <button type="button" onClick={() => onPrimary(index)} className="col-span-2 min-h-[34px] rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800">Set as Primary Cover</button> : null}
                </div>
              </div>
            </div>
          );
        })}
        {!items.length ? <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm font-semibold text-slate-500">No media selected yet. Local previews appear immediately after file selection.</div> : null}
      </div>
      <p className="mt-2 text-xs font-semibold text-slate-500">{items.length}/{max} selected</p>
    </div>
  );
}

export function PackageDocumentUploader({ items, max, onAdd, onLabelChange, onDelete, error }) {
  const inputRef = useRef(null);
  const handleAddFiles = (event) => {
    try {
      onAdd(Array.from(event.target.files || []));
    } catch (selectionError) {
      console.error("Unable to select package/menu files.", selectionError);
    } finally {
      event.target.value = "";
    }
  };
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className={labelClass}>Packages / Menu PDF (optional)</p>
          <p className="-mt-1 text-xs font-semibold text-slate-500">Upload pricing packages or the food menu as PDF (max 10MB each). Visible to visitors as instant view/download links — no forms required.</p>
        </div>
        <button type="button" onClick={() => inputRef.current?.click()} className="inline-flex min-h-[40px] shrink-0 touch-manipulation items-center gap-2 rounded-xl bg-slate-950 px-4 py-2 text-sm font-bold text-white">
          <FaCloudArrowUp /> Add PDF
        </button>
        <input ref={inputRef} type="file" multiple accept="application/pdf" className="hidden" onChange={handleAddFiles} />
      </div>
      {error ? <p className="mb-3 text-xs font-bold text-rose-700">{error}</p> : null}
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={item.id || item.url || index} className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-rose-50 text-xl text-rose-600"><FaFilePdf /></span>
            <div className="min-w-[200px] flex-1">
              <input
                className={inputClass}
                value={item.label || ""}
                onChange={(event) => onLabelChange(index, event.target.value)}
                placeholder="Label, e.g. Wedding Package"
              />
              <p className="mt-1 truncate text-xs font-semibold text-slate-500">{item.fileName}</p>
              {item.status === "uploading" ? <p className="text-xs font-bold text-sky-700">Uploading{item.progress ? ` ${item.progress}%` : "..."} </p> : null}
              {item.status === "failed" ? <p className="break-words text-xs font-bold text-rose-700">{item.error || "Upload failed"}</p> : null}
            </div>
            <button type="button" onClick={() => onDelete(index)} className="min-h-[36px] shrink-0 rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-bold text-rose-700">Remove</button>
          </div>
        ))}
        {!items.length ? <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm font-semibold text-slate-500">No PDFs uploaded yet.</div> : null}
      </div>
      <p className="mt-2 text-xs font-semibold text-slate-500">{items.length}/{max} selected</p>
    </div>
  );
}

const dateSlotChoices = [
  { status: "lunchBooked", label: "Lunch" },
  { status: "dinnerBooked", label: "Dinner" },
  { status: "fullDayBooked", label: "Full Day" },
  { status: "blocked", label: "Blocked" },
  { status: "available", label: "Mark Available" },
];

export function OwnerCalendar({ value, onChange }) {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectionStart, setSelectionStart] = useState("");
  const [selectionEnd, setSelectionEnd] = useState("");
  const [bulkStatus, setBulkStatus] = useState("available");
  const [popupDateKey, setPopupDateKey] = useState("");
  const dates = value?.dates || {};
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startOffset = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - startOffset);
  const days = Array.from({ length: 42 }, (_, index) => {
    const item = new Date(start);
    item.setDate(start.getDate() + index);
    return item;
  });
  const applyDates = (nextDates) => {
    onChange({ ...value, dates: nextDates, bookedDates: nextDates, bookedSlots: toBookedSlots(nextDates) });
  };
  const setDateStatus = (key, nextStatus) => {
    const nextDates = { ...dates };
    if (nextStatus === "available") {
      delete nextDates[key];
    } else {
      nextDates[key] = nextStatus;
    }
    applyDates(nextDates);
    setPopupDateKey("");
  };
  const applyRange = () => {
    if (!selectionStart) return;
    const startDate = new Date(selectionStart);
    const endDate = new Date(selectionEnd || selectionStart);
    const nextDates = { ...dates };
    for (let day = new Date(startDate); day <= endDate; day.setDate(day.getDate() + 1)) {
      const key = dateKey(day);
      if (bulkStatus === "available") {
        delete nextDates[key];
      } else {
        nextDates[key] = bulkStatus;
      }
    }
    applyDates(nextDates);
  };
  const counters = Object.values(dates).reduce((acc, item) => ({ ...acc, [item]: (acc[item] || 0) + 1 }), {});
  const popupDate = popupDateKey ? new Date(popupDateKey) : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="rounded-xl border border-slate-200 p-3"><FaArrowLeft /></button>
          <div className="rounded-xl bg-slate-100 px-4 py-3 text-sm font-black text-slate-800">{month.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</div>
          <button type="button" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="rounded-xl border border-slate-200 p-3"><FaArrowRight /></button>
        </div>
        <Toggle label="Show Availability To Public" value={value?.showToPublic !== false} onChange={(next) => onChange({ ...value, showToPublic: next })} />
      </div>
      <p className="text-xs font-semibold text-slate-500">Click any date below to set it as Lunch, Dinner, Full Day booked, or Blocked.</p>
      <div className="grid grid-cols-4 gap-2 text-xs font-bold sm:grid-cols-5">
        <span>Lunch: {counters.lunchBooked || 0}</span>
        <span>Dinner: {counters.dinnerBooked || 0}</span>
        <span>Full Day: {counters.fullDayBooked || 0}</span>
        <span>Blocked: {counters.blocked || 0}</span>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs font-bold uppercase text-slate-400">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => <div key={day}>{day}</div>)}</div>
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {days.map((day) => {
          const key = dateKey(day);
          const dateStatus = dates[key] || "available";
          const muted = day.getMonth() !== month.getMonth();
          return (
            <button type="button" key={key} onClick={() => setPopupDateKey(key)} className={`min-h-[54px] rounded-xl border p-1 text-left text-xs font-bold sm:min-h-[76px] ${muted ? "opacity-40" : ""} ${popupDateKey === key ? "ring-2 ring-emerald-500" : ""} ${statusStyles[dateStatus] || statusStyles.available}`}>
              <span>{day.getDate()}</span>
              <span className="mt-1 block hidden sm:block">{statusLabels[dateStatus]}</span>
            </button>
          );
        })}
      </div>

      {popupDate ? (
        <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-black text-slate-900">{popupDate.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</p>
              <p className="text-xs font-semibold text-slate-600">Current status: {statusLabels[dates[popupDateKey] || "available"]}</p>
            </div>
            <button type="button" onClick={() => setPopupDateKey("")} className="min-h-[36px] rounded-lg border border-slate-300 bg-white px-3 text-xs font-bold text-slate-700">Close</button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {dateSlotChoices.map((choice) => (
              <button
                key={choice.status}
                type="button"
                onClick={() => setDateStatus(popupDateKey, choice.status)}
                className={`min-h-[40px] rounded-xl border px-4 text-sm font-bold transition ${(dates[popupDateKey] || "available") === choice.status ? "border-slate-950 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
              >
                {choice.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <p className="mb-3 text-xs font-black uppercase tracking-[0.14em] text-slate-500">Bulk range update (optional)</p>
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto]">
          <TextField label="Start date" type="date" value={selectionStart} onChange={setSelectionStart} />
          <TextField label="End date" type="date" value={selectionEnd} onChange={setSelectionEnd} />
          <SelectField label="Status" value={bulkStatus} onChange={setBulkStatus} options={Object.keys(statusLabels)} />
          <button type="button" onClick={applyRange} className="self-end rounded-xl bg-slate-950 px-5 py-3 text-sm font-black text-white">Apply</button>
        </div>
      </div>
    </div>
  );
}

